// A success test from the dice bag (issue 1523; docs/MRCS/shadowrun-3.md §5.1): only where the table's system names a
// test (`features.combat.test`), so a d20 table never sees it. A player tests a linked character of their own, the DM
// any token or someone typed: a skill, a specialization or an attribute read from the character's sheet, a skill it
// lacks through a defaulting route its content links allow, or a rating typed in; the TN (the difficulty table as a
// hint) and its modifiers; an open test, or an opposed one with the other side typed. The browser names what is tested
// and nothing the server decides: the server throws the dice, checks the default and works the successes out.
// Pool dice (issue 1524; §5.2): a token in the turn order may add dice from one of its pools that is added as dice, for
// a kind of test the pool names, at most its cap (and a default's) and what is left; the server checks the same and
// takes them off. The working line then reads "Stealth 5 + 2 Combat Pool = 7 dice". A token in no turn order, or
// someone typed, has no pool: its dice are its rating alone.
import { useEffect, useMemo, useState } from 'react';
import {
  POOL_FOR_WORDS, TEST_LIMITS, KARMA_WORDS, addsDice, karmaCost, karmaRefusal, poolCap, woundsOf,
  type PoolUse, type RollRecord, type Values, type LackedSkill, type PoolFor, type SeenPool, type TestChoice, type TestRoute,
} from '@dragons-table/vtt-shared';
import { selection } from '../../board/selection';
import { fetchTests, type TestsAnswer } from '../../entries';
import { useTableState } from '../../table/TableState';
import { useWoundRules } from '../../table/numberWords';
import { useShell } from '../context';
import { BTN } from '../ui';

const btn = BTN;
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';

interface TokenLike { id: string; kind: string; name?: string; mine?: boolean; character?: string | null }

/** Someone typed, with no token (the DM's). */
const SOMEONE = 'someone';
/** A rating typed in. */
const TYPED = 'typed';
/** The difficulty table, as a hint beside the TN (the rules reference page has it whole). */
export const DIFFICULTY_HINT = 'TN 2 simple … 4 average … 10+ nearly impossible';

const int = (v: string): number | null => (v.trim() !== '' && Number.isInteger(Number(v)) ? Number(v) : null);
const routeText = (r: TestRoute): string => `${r.rated.name} ${r.rated.rating}, +${r.target} TN${r.cap === null ? ', no pool dice' : `, at most ${r.cap} pool ${r.cap === 1 ? 'die' : 'dice'}`}`;

export function TestForm({ isDm, priv, onClose }: { isDm: boolean; priv: boolean; onClose: () => void }) {
  const { store, act, setNotice, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.test;
  const tokens = Object.values(store.state.objects as Record<string, TokenLike>).filter((o) => o.kind === 'token');
  // A player's own linked characters; the DM's every token they see, and someone typed.
  const mine = tokens.filter((o) => (isDm ? true : o.mine && o.character));
  const picked = [...selection.get()].find((id) => mine.some((o) => o.id === id));
  const [who, setWho] = useState(picked ?? mine[0]?.id ?? (isDm ? SOMEONE : ''));
  const [whoName, setWhoName] = useState('');
  const [answer, setAnswer] = useState<TestsAnswer | 'loading'>(null);
  const [what, setWhat] = useState(TYPED);
  const [route, setRoute] = useState(0);
  const [typedName, setTypedName] = useState('');
  const [typedRating, setTypedRating] = useState('3');
  const [target, setTarget] = useState('4');
  const [mods, setMods] = useState<Array<{ label: string; value: string }>>([]);
  const [open, setOpen] = useState(false);
  const [opposed, setOpposed] = useState(false);
  const [opName, setOpName] = useState('');
  const [opRating, setOpRating] = useState('3');
  const [opTarget, setOpTarget] = useState('4');
  const [poolKey, setPoolKey] = useState('');
  const [poolFor, setPoolFor] = useState<PoolFor | ''>('');
  const [poolDice, setPoolDice] = useState('0');
  const [resist, setResist] = useState(false);
  // The tested token's wounds (issue 1526), from the numbers this person is sent: the server joins them to the TN.
  const woundRules = useWoundRules();

  const token = tokens.find((o) => o.id === who);
  const wound = woundsOf(woundRules, (store.state.objects as Record<string, { values?: Values }>)[who]?.values?.resources).target;
  const character = token?.character ?? null;
  useEffect(() => {
    setAnswer(null); setWhat(TYPED); setRoute(0);
    if (!character || who === SOMEONE) return undefined;
    let live = true;
    setAnswer('loading');
    void fetchTests(campaignId, who, scene?.id ?? null).then((a) => {
      if (!live) return;
      setAnswer(a);
      if (a?.choices.length) setWhat('c:0');
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, character, campaignId]);

  const got = answer && answer !== 'loading' ? answer : null;
  const choice: TestChoice | null = what.startsWith('c:') ? got?.choices[Number(what.slice(2))] ?? null : null;
  const lacked: LackedSkill | null = what.startsWith('l:') ? got?.lacked[Number(what.slice(2))] ?? null : null;
  const chosenRoute: TestRoute | null = lacked ? lacked.routes[route] ?? null : null;
  const terms = useMemo(() => mods.flatMap((m) => { const v = int(m.value); return m.label.trim() && v !== null && v !== 0 ? [{ label: m.label.trim().slice(0, TEST_LIMITS.label), value: Math.max(-TEST_LIMITS.modifier, Math.min(TEST_LIMITS.modifier, v)) }] : []; }), [mods]);
  const tn = int(target);
  const beforeDefault = tn === null ? null : tn + terms.reduce((n, t) => n + t.value, 0);
  const limit = view?.defaulting?.limit ?? null;
  const overLimit = Boolean(lacked && !open && limit !== null && beforeDefault !== null && beforeDefault >= limit);
  const rating = int(typedRating);
  const rated = choice ? { kind: choice.kind, name: choice.name, rating: choice.rating, ...(choice.ref ? { ref: choice.ref } : {}) }
    : chosenRoute ? { kind: chosenRoute.rated.kind, name: chosenRoute.rated.name, rating: chosenRoute.rated.rating, ...(chosenRoute.rated.ref ? { ref: chosenRoute.rated.ref } : {}) }
      : what === TYPED && typedName.trim() && rating !== null && rating >= 1 && rating <= TEST_LIMITS.rating ? { kind: 'typed' as const, name: typedName.trim().slice(0, TEST_LIMITS.name), rating } : null;
  const opR = int(opRating); const opT = int(opTarget);
  const opReady = !opposed || (opName.trim() !== '' && opR !== null && opR >= 1 && opR <= TEST_LIMITS.rating && opT !== null && opT >= 1 && opT <= TEST_LIMITS.target);
  // The tested token's combatant and its pools (issue 1524): only what this person is sent, their own or the DM's.
  const enc = store.state.encounter as { combatants?: Array<{ tokens: string[]; pools?: Record<string, SeenPool> }> } | undefined;
  const fighting = who !== SOMEONE ? enc?.combatants?.find((c) => c.tokens.includes(who)) : undefined;
  // A pool with nothing left in it (a size of 0, or spent) is no choice to draw on (qa-runs 148); the DM still sets sizes in the tracker.
  const spendable = (systemRules.pools ?? []).filter((r) => addsDice(r) && (fighting?.pools?.[r.key]?.left ?? 0) > 0);
  const poolRule = spendable.find((r) => r.key === poolKey) ?? null;
  const kind: PoolFor | null = poolRule ? (poolFor && poolRule.for.includes(poolFor) ? poolFor : poolRule.for[0]!) : null;
  const left = poolRule ? fighting!.pools![poolRule.key]!.left : 0;
  const base = chosenRoute?.to === 'specialization' ? got?.choices.find((c) => c.kind === 'skill' && c.ref === chosenRoute.rated.ref)?.rating : undefined;
  const cap = poolRule && kind && rated ? poolCap(poolRule, kind, rated.rating, chosenRoute ? { pool: chosenRoute.pool, ...(base !== undefined ? { base } : {}) } : undefined) : null;
  const most = Math.min(left, cap ?? left);
  const poolN = int(poolDice) ?? 0;
  const poolBad = Boolean(poolRule) && (poolN < 0 || poolN > most);
  const ready = who !== '' && rated !== null && (open || (tn !== null && tn >= 1 && tn <= TEST_LIMITS.target)) && !overLimit && opReady && !(lacked && !chosenRoute) && !poolBad;

  if (!view) return null;
  if (mine.length === 0 && !isDm) {
    return (
      <div className="rounded border border-stone-700 p-2 text-xs text-stone-400" data-testid="test-form">
        You have no character at the table to test.
        <button type="button" className={`${btn} ml-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  return (
    <form className="space-y-1 rounded border border-sky-700 p-2 text-xs" data-testid="test-form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready || !rated) return;
      const body = {
        kind: 'test.request',
        ...(who === SOMEONE ? (whoName.trim() ? { name: whoName.trim().slice(0, TEST_LIMITS.name) } : {}) : { token: who }),
        rated, ...(lacked && chosenRoute ? { defaulted: { skill: lacked.ref, name: lacked.name, to: chosenRoute.to, ...(base !== undefined ? { base } : {}) } } : {}),
        ...(open ? {} : { target: tn }), modifiers: open ? [] : terms, open,
        ...(opposed && !open ? { opposed: { name: opName.trim().slice(0, TEST_LIMITS.name), rating: opR, target: opT, modifiers: [] } } : {}),
        private: isDm ? priv : false,
        ...(poolRule && kind && poolN > 0 ? { pool: { key: poolRule.key, dice: poolN, for: kind } } : {}),
        ...(resist && !open ? { resist: true } : {}),
      };
      act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That test could not be made.'); });
    }}>
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Who
          <select aria-label="Who is tested" className={field} value={who} onChange={(e) => setWho(e.target.value)}>
            {mine.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed token'}</option>)}
            {isDm && <option value={SOMEONE}>Someone else…</option>}
          </select>
        </label>
        {who === SOMEONE && <input aria-label="Their name" placeholder="Guard" maxLength={TEST_LIMITS.name} className={`${field} w-24`} value={whoName} onChange={(e) => setWhoName(e.target.value)} />}
      </div>
      {answer === 'loading' && <p className="text-stone-400">Reading the character's sheet…</p>}
      {got?.notice && <p className="text-amber-300" data-testid="test-notice">{got.notice}</p>}
      <label className="flex items-center gap-1">Test
        <select aria-label="What is tested" className={field} value={what} onChange={(e) => { setWhat(e.target.value); setRoute(0); }}>
          {got?.choices.map((c, i) => <option key={`c:${i}`} value={`c:${i}`}>{c.name} {c.rating}</option>)}
          {got && got.lacked.length > 0 && (
            <optgroup label="Without the skill (defaulting)">
              {got.lacked.map((l, i) => <option key={`l:${i}`} value={`l:${i}`}>{l.name}{l.routes.length ? '' : ' (no default)'}</option>)}
            </optgroup>
          )}
          <option value={TYPED}>A rating typed in…</option>
        </select>
      </label>
      {lacked && (lacked.routes.length
        ? (
          <label className="flex items-center gap-1" data-testid="test-routes">Default to
            <select aria-label="Default to" className={field} value={String(route)} onChange={(e) => setRoute(Number(e.target.value))}>
              {lacked.routes.map((r, i) => <option key={i} value={String(i)}>{routeText(r)}</option>)}
            </select>
          </label>
        )
        : <p className="text-amber-300" data-testid="test-no-default">{lacked.name} cannot be defaulted: the character needs the skill.</p>)}
      {what === TYPED && (
        <div className="flex flex-wrap items-center gap-1" data-testid="test-typed">
          <input aria-label="Rating name" placeholder="Climbing" maxLength={TEST_LIMITS.name} className={`${field} w-28`} value={typedName} onChange={(e) => setTypedName(e.target.value)} />
          <label className="flex items-center gap-1">rating <input aria-label="Rating" type="number" min={1} max={TEST_LIMITS.rating} className={`${field} w-12`} value={typedRating} onChange={(e) => setTypedRating(e.target.value)} /></label>
        </div>
      )}
      {spendable.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" data-testid="test-pool">
          <label className="flex items-center gap-1">Extra dice from
            <select aria-label="Pool" className={field} value={poolRule?.key ?? ''} onChange={(e) => { setPoolKey(e.target.value); setPoolFor(''); setPoolDice('0'); }}>
              <option value="">None</option>
              {spendable.map((r) => <option key={r.key} value={r.key}>{r.label} ({fighting!.pools![r.key]!.left} left)</option>)}
            </select>
          </label>
          {poolRule && poolRule.for.length > 1 && (
            <select aria-label="Drawn for" className={field} value={kind ?? ''} onChange={(e) => setPoolFor(e.target.value as PoolFor)}>
              {poolRule.for.map((k) => <option key={k} value={k}>{POOL_FOR_WORDS[k]}</option>)}
            </select>
          )}
          {poolRule && (
            <>
              <input aria-label="Pool dice" type="number" min={0} max={most} className={`${field} w-12`} value={poolDice} onChange={(e) => setPoolDice(e.target.value)} />
              <span className="text-stone-400" data-testid="test-pool-most">at most {most}{cap !== null && cap < left ? ` (the cap on ${kind ? POOL_FOR_WORDS[kind].toLowerCase() : 'this test'})` : ''}</span>
            </>
          )}
          {poolBad && <p className="text-amber-300" data-testid="test-pool-over">At most {most} {poolRule!.label} {most === 1 ? 'die' : 'dice'} may join this test.</p>}
        </div>
      )}
      {view.open && (
        <label className="flex items-center gap-1" title="Roll the rating with no TN and keep the highest die, its 6s rolled again and added.">
          <input type="checkbox" aria-label="Open test" checked={open} onChange={(e) => { setOpen(e.target.checked); if (e.target.checked) setOpposed(false); }} /> Open test
        </label>
      )}
      {!open && (
        <>
          <div className="flex flex-wrap items-center gap-1">
            <label className="flex items-center gap-1">TN <input aria-label="Target number" type="number" min={1} max={TEST_LIMITS.target} className={`${field} w-12`} value={target} onChange={(e) => setTarget(e.target.value)} /></label>
            <span className="text-stone-400" data-testid="test-difficulty">{DIFFICULTY_HINT}</span>
          </div>
          {mods.map((m, i) => (
            <div key={i} className="flex items-center gap-1" data-testid={`test-modifier-${i + 1}`}>
              <input aria-label={`Modifier ${i + 1} label`} placeholder="wounded" maxLength={TEST_LIMITS.label} className={`${field} w-24`} value={m.label} onChange={(e) => setMods((cur) => cur.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <input aria-label={`Modifier ${i + 1} to the TN`} type="number" className={`${field} w-12`} value={m.value} onChange={(e) => setMods((cur) => cur.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
              <button type="button" className={btn} aria-label={`Remove modifier ${i + 1}`} onClick={() => setMods((cur) => cur.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <button type="button" className={btn} disabled={mods.length >= TEST_LIMITS.modifiers} onClick={() => setMods((cur) => [...cur, { label: '', value: '1' }])}>Add a TN modifier</button>
          {overLimit && <p className="text-amber-300" data-testid="test-over-limit">No default is allowed at a TN of {limit} or more.</p>}
          {woundRules.length > 0 && (
            <label className="flex items-center gap-1" title="Wounds move the TN of every test but one to resist damage.">
              <input type="checkbox" aria-label="Damage resistance test" checked={resist} onChange={(e) => setResist(e.target.checked)} /> Resisting damage (no wound modifier)
            </label>
          )}
          {wound !== 0 && !resist && !(kind === 'resist') && <p className="text-amber-300" data-testid="test-wounded">Wounded: the table adds {wound > 0 ? '+' : '−'}{Math.abs(wound)} to the TN.</p>}
          <label className="flex items-center gap-1">
            <input type="checkbox" aria-label="Opposed test" checked={opposed} onChange={(e) => setOpposed(e.target.checked)} /> Opposed by…
          </label>
          {opposed && (
            <div className="flex flex-wrap items-center gap-1" data-testid="test-opposed">
              <input aria-label="Opposed by" placeholder="Guard" maxLength={TEST_LIMITS.name} className={`${field} w-24`} value={opName} onChange={(e) => setOpName(e.target.value)} />
              <label className="flex items-center gap-1">rating <input aria-label="Their rating" type="number" min={1} max={TEST_LIMITS.rating} className={`${field} w-12`} value={opRating} onChange={(e) => setOpRating(e.target.value)} /></label>
              <label className="flex items-center gap-1">TN <input aria-label="Their target number" type="number" min={1} max={TEST_LIMITS.target} className={`${field} w-12`} value={opTarget} onChange={(e) => setOpTarget(e.target.value)} /></label>
            </div>
          )}
        </>
      )}
      <div className="flex gap-1">
        <button type="submit" className={`${btn} border-sky-600`} disabled={!ready}>Roll the test</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && <span className="self-center text-stone-400">{priv ? 'Private: only you see it.' : 'Public: the table sees it.'}</span>}
      </div>
    </form>
  );
}

/**
 * A resolved test's Karma Pool uses (issue 1529; docs/MRCS/shadowrun-3.md §5.2): offered to the test's maker (and the
 * DM) when the tested token's combatant carries a pool with uses and some of it is left — *Re-roll the failures (1)*, then
 * *(2 more)*; *Buy a die*; *Burn 1 for a success*; on a disaster, *Avoid the disaster (1)*. Each button names its cost as
 * the record's place makes it; one the rules refuse now is not offered. The server decides: it throws and folds the use.
 */
export function KarmaParts({ roll, meId, isDm }: { roll: RollRecord; meId: string; isDm: boolean }) {
  const { store, act, setNotice, systemRules } = useTableState();
  const [sent, setSent] = useState(false);
  const f = roll.test;
  const token = f?.sides[0]?.token;
  const enc = store.state.encounter as { combatants?: Array<{ tokens: string[]; pools?: Record<string, SeenPool> }> } | undefined;
  const pools = token ? enc?.combatants?.find((c) => c.tokens.includes(token))?.pools : undefined;
  const rule = (systemRules.pools ?? []).find((p) => p.uses.length && pools?.[p.key]);
  if (!f || f.open || !rule || !pools || (!isDm && roll.actor.id !== meId)) return null;
  const left = pools[rule.key]!.left;
  const offers = rule.uses.filter((u) => karmaRefusal(f, u, left) === null);
  if (!offers.length) return null;
  const earlier = (u: PoolUse) => (f.karma ?? []).some((k) => k.use === u) && (u === 'reroll_failures' || u === 'buy_dice');
  const send = (use: PoolUse) => {
    setSent(true);
    act({ kind: 'test.karma', rollId: roll.id, use } as never, (reply) => { setSent(false); if (!reply.ok) setNotice(reply.reason ?? 'That could not be spent.'); });
  };
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 font-sans text-[0.625rem]" data-testid={`karma-${roll.id}`}>
      <span className="text-stone-400">{rule.label} ({left} left):</span>
      {offers.map((u) => {
        const cost = karmaCost(f, u);
        return <button key={u} type="button" className={btn} disabled={sent} onClick={() => send(u)}>{KARMA_WORDS[u]} ({cost}{earlier(u) ? ' more' : ''})</button>;
      })}
    </div>
  );
}
